import { apiRequest } from "./apiClient";

const DEFAULT_RESERVATION_RULES = {
  tableServices: [
    {
      id: "midi",
      label: "Midi",
      start: "11:00",
      end: "13:00",
      maxCovers: 40,
      maxCoversPerSlot: 8,
      slotIntervalMinutes: 5,
    },
    {
      id: "soir",
      label: "Soir",
      start: "18:00",
      end: "20:00",
      maxCovers: 40,
      maxCoversPerSlot: 8,
      slotIntervalMinutes: 5,
    },
  ],
  roomOptions: [
    { id: "chambre-1", label: "Chambre 1", isActive: true },
    { id: "chambre-2", label: "Chambre 2", isActive: true },
    { id: "chambre-3", label: "Chambre 3", isActive: true },
  ],
};

function normalizeReservationRules(input) {
  if (!input || typeof input !== "object") {
    const maxCoversPerDay = DEFAULT_RESERVATION_RULES.tableServices.reduce(
      (total, service) => total + service.maxCovers,
      0,
    );

    return {
      ...DEFAULT_RESERVATION_RULES,
      serviceWindows: DEFAULT_RESERVATION_RULES.tableServices.map(
        (service) => ({
          start: service.start,
          end: service.end,
        }),
      ),
      maxCoversPerDay,
    };
  }

  const fallbackMax = Number.isInteger(
    Number.parseInt(input.maxCoversPerDay, 10),
  )
    ? Number.parseInt(input.maxCoversPerDay, 10)
    : 40;

  const tableServicesInput = Array.isArray(input.tableServices)
    ? input.tableServices
    : Array.isArray(input.serviceWindows)
      ? input.serviceWindows.map((windowItem, index) => ({
          id: index === 0 ? "midi" : `service-${index + 1}`,
          label:
            index === 0
              ? "Midi"
              : index === 1
                ? "Soir"
                : `Service ${index + 1}`,
          start: String(windowItem?.start || ""),
          end: String(windowItem?.end || ""),
          maxCovers: fallbackMax,
        }))
      : DEFAULT_RESERVATION_RULES.tableServices;

  const tableServices = tableServicesInput
    .map((serviceItem, index) => {
      const maxValue = Number.parseInt(serviceItem?.maxCovers, 10);
      const maxPerSlotValue = Number.parseInt(
        serviceItem?.maxCoversPerSlot,
        10,
      );
      const intervalValue = Number.parseInt(
        serviceItem?.slotIntervalMinutes,
        10,
      );
      return {
        id: String(serviceItem?.id || `service-${index + 1}`).trim(),
        label: String(serviceItem?.label || `Service ${index + 1}`).trim(),
        start: String(serviceItem?.start || "").trim(),
        end: String(serviceItem?.end || "").trim(),
        maxCovers:
          Number.isInteger(maxValue) && maxValue >= 1 && maxValue <= 500
            ? maxValue
            : fallbackMax,
        maxCoversPerSlot:
          Number.isInteger(maxPerSlotValue) &&
          maxPerSlotValue >= 1 &&
          maxPerSlotValue <= 100
            ? maxPerSlotValue
            : 8,
        slotIntervalMinutes:
          Number.isInteger(intervalValue) &&
          intervalValue >= 5 &&
          intervalValue <= 60
            ? intervalValue
            : 5,
      };
    })
    .filter(
      (serviceItem) =>
        serviceItem.id &&
        serviceItem.label &&
        serviceItem.start &&
        serviceItem.end,
    );

  const roomOptionsInput = Array.isArray(input.roomOptions)
    ? input.roomOptions
    : DEFAULT_RESERVATION_RULES.roomOptions;

  const roomOptions = roomOptionsInput
    .map((roomItem, index) => ({
      id: String(roomItem?.id || `chambre-${index + 1}`).trim(),
      label: String(roomItem?.label || `Chambre ${index + 1}`).trim(),
      isActive: roomItem?.isActive !== false,
    }))
    .filter((roomItem) => roomItem.id && roomItem.label);

  const safeServices =
    tableServices.length > 0
      ? tableServices
      : DEFAULT_RESERVATION_RULES.tableServices;
  const safeRooms =
    roomOptions.length > 0
      ? roomOptions
      : DEFAULT_RESERVATION_RULES.roomOptions;

  const maxCoversPerDay = safeServices.reduce(
    (total, serviceItem) => total + serviceItem.maxCovers,
    0,
  );

  return {
    maxCoversPerDay,
    serviceWindows: safeServices.map((serviceItem) => ({
      start: serviceItem.start,
      end: serviceItem.end,
    })),
    tableServices: safeServices,
    roomOptions: safeRooms,
  };
}

export async function fetchReservationPublicSettings() {
  const data = await apiRequest("/content/reservation-settings");
  return normalizeReservationRules(data?.reservationRules);
}

export async function createReservation(payload) {
  const data = await apiRequest("/reservations", {
    method: "POST",
    body: payload,
  });

  return {
    reservationId: data?.reservationId || null,
    status: data?.status || "pending",
  };
}

export async function fetchReservationDepositPolicy() {
  const data = await apiRequest("/reservations/deposit-policy");
  return (
    data?.depositPolicy || {
      enabled: false,
      currency: "eur",
      tablePerGuestCents: 0,
      roomPerNightCents: 0,
    }
  );
}

export async function createReservationDepositIntent(payload) {
  const data = await apiRequest("/reservations/deposit-intent", {
    method: "POST",
    body: payload,
  });

  return data?.deposit || null;
}

export async function fetchReservationAvailabilityPublic(options = {}) {
  const from = options.from || "";
  const days = options.days || 45;
  const query = new URLSearchParams();
  if (from) {
    query.set("from", String(from));
  }
  query.set("days", String(days));

  const data = await apiRequest(
    `/reservations/availability?${query.toString()}`,
  );
  return Array.isArray(data?.availability) ? data.availability : [];
}

export async function fetchReservationsForAdmin(options = {}) {
  const status = options.status || "all";
  const reservationType = options.reservationType || "all";
  const requestedDate = options.requestedDate || "";
  const sort = options.sort || "requestedDateAsc";
  const limit = options.limit || 100;

  const query = new URLSearchParams();
  query.set("status", status);
  query.set("reservationType", reservationType);
  if (requestedDate) {
    query.set("requestedDate", requestedDate);
  }
  query.set("sort", sort);
  query.set("limit", String(limit));

  const data = await apiRequest(`/admin/reservations?${query.toString()}`);
  return Array.isArray(data?.reservations) ? data.reservations : [];
}

export async function fetchReservationSettingsForAdmin() {
  const data = await apiRequest("/admin/reservations/settings");
  return normalizeReservationRules(data?.reservationRules);
}

export async function saveReservationSettingsForAdmin(reservationRules) {
  const data = await apiRequest("/admin/reservations/settings", {
    method: "PUT",
    body: { reservationRules },
  });

  return normalizeReservationRules(data?.reservationRules);
}

export async function createReservationByAdmin(payload) {
  const data = await apiRequest("/admin/reservations", {
    method: "POST",
    body: payload,
  });

  return data?.reservation || null;
}

export async function updateReservationStatus(reservationId, status) {
  const data = await apiRequest(`/admin/reservations/${reservationId}/status`, {
    method: "PATCH",
    body: { status },
  });

  return data?.reservation || null;
}

export async function deleteReservationForAdmin(reservationId) {
  const data = await apiRequest(`/admin/reservations/${reservationId}`, {
    method: "DELETE",
  });

  return data?.deletedId || null;
}

export async function fetchReservationAnalytics(options = {}) {
  const days = options.days || 30;
  const query = new URLSearchParams();
  query.set("days", String(days));

  const data = await apiRequest(
    `/admin/reservations/analytics?${query.toString()}`,
  );
  return data?.analytics || null;
}
